import { useEffect, useRef, useState } from 'react';
import { FaCheck, FaCloudUploadAlt, FaDownload, FaExclamationTriangle, FaEye, FaFile, FaFileAlt, FaRedo, FaTimes } from 'react-icons/fa';
import { AppButton } from '../Button/_index';
import { AppModal } from '../Modal/_index';
import { AppLightbox } from '../Gallery/_index';
import { Progress } from '../Kit/_index';
import { UPLOAD_STATUS, onFormatBytes } from './useUploader';

const BLOCK = 'LStudio__app_uploader';

const STATUS_ICON = {
    [UPLOAD_STATUS.DONE]: <FaCheck />,
    [UPLOAD_STATUS.ERROR]: <FaExclamationTriangle />,
    [UPLOAD_STATUS.CANCELED]: <FaTimes />,
};

const STATUS_TONE = {
    [UPLOAD_STATUS.DONE]: 'ok',
    [UPLOAD_STATUS.ERROR]: 'danger',
    [UPLOAD_STATUS.CANCELED]: 'neutral',
    [UPLOAD_STATUS.UPLOADING]: 'accent',
    [UPLOAD_STATUS.PENDING]: 'neutral',
};

const onIsImage = (item) => String(item?.type ?? '').startsWith('image/');
const onIsPdf = (item) => String(item?.type ?? '').includes('pdf') || String(item?.name ?? '').toLowerCase().endsWith('.pdf');
const onIsVideo = (item) => String(item?.type ?? '').startsWith('video/');
const onIsAudio = (item) => String(item?.type ?? '').startsWith('audio/');
const onIsText = (item) => String(item?.type ?? '').startsWith('text/') || /\.(txt|md|json|csv|log|xml|html|css|js|ts|jsx|tsx)$/i.test(String(item?.name ?? ''));

export const AppUploader = ({
    uploader,
    accept = null,
    isMultiple = true,
    title = 'Arrastrá archivos o hacé clic',
    hint = null,
    variant = 'drop',
    showList = true,
    showFileActions = true,
}) => {
    const REF__useRefInput = useRef(null);
    const [useDepth, setDepth] = useState(0);
    const [useNameViewerItemState, setNameViewerItemState] = useState(null);
    const [useNameViewerUrlState, setNameViewerUrlState] = useState('');
    const [useNameViewerModeState, setNameViewerModeState] = useState('file');

    const cb__closeViewerCallback = () => {
        if (useNameViewerUrlState) URL.revokeObjectURL(useNameViewerUrlState);
        setNameViewerUrlState('');
        setNameViewerItemState(null);
    };

    useEffect(() => () => {
        if (useNameViewerUrlState) URL.revokeObjectURL(useNameViewerUrlState);
    }, [useNameViewerUrlState]);

    const cb__openViewerCallback = (item, mode = 'file') => {
        if (!item?.file) return;
        if (useNameViewerUrlState) URL.revokeObjectURL(useNameViewerUrlState);
        setNameViewerUrlState(URL.createObjectURL(item.file));
        setNameViewerItemState(item);
        setNameViewerModeState(mode);
    };

    const cb__downloadCallback = (item) => {
        if (!item?.file) return;
        const URL_FILE = URL.createObjectURL(item.file);
        const LINK = document.createElement('a');
        LINK.href = URL_FILE;
        LINK.download = item.name || 'archivo';
        document.body.appendChild(LINK);
        LINK.click();
        LINK.remove();
        setTimeout(() => URL.revokeObjectURL(URL_FILE), 0);
    };

    const onDrop = (event) => {
        event.preventDefault();
        setDepth(0);
        uploader.onAdd(event.dataTransfer.files);
    };

    const onPick = (event) => {
        uploader.onAdd(event.target.files);
        event.target.value = '';
    };

    const VIEWER_ITEM = useNameViewerItemState;

    return (
        <div className={BLOCK} data-variant={variant}>
            <div className={`${BLOCK}__drop`} data-over={useDepth > 0} role="button" tabIndex={0}
                onClick={() => REF__useRefInput.current?.click()}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    REF__useRefInput.current?.click();
                }}
                onDragEnter={(e) => { e.preventDefault(); setDepth((d) => d + 1); }}
                onDragLeave={() => setDepth((d) => Math.max(0, d - 1))}
                onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
                <span className={`${BLOCK}__drop_icon`} aria-hidden="true"><FaCloudUploadAlt /></span>
                <p className={`${BLOCK}__drop_title`}>{title}</p>
                {hint && <p className={`${BLOCK}__drop_hint`}>{hint}</p>}
                <input ref={REF__useRefInput} type="file" className={`${BLOCK}__input`} multiple={isMultiple}
                    accept={Array.isArray(accept) ? accept.join(',') : (accept ?? undefined)} onChange={onPick} tabIndex={-1} />
            </div>

            {uploader.total > 0 && (
                <div className={`${BLOCK}__summary`}>
                    <span className={`${BLOCK}__count`}>{uploader.done} de {uploader.total} subidos</span>
                    <AppButton variant="ghost" tone="neutral" size="sm" onClick={uploader.onClear}>Vaciar</AppButton>
                </div>
            )}

            {showList && uploader.items.length > 0 && (
                <ul className={`${BLOCK}__list`}>
                    {uploader.items.map((item) => (
                        <li key={item.id} className={`${BLOCK}__item`} data-tone={STATUS_TONE[item.status]} data-status={item.status}>
                            <span className={`${BLOCK}__thumb`} aria-hidden="true">
                                {item.preview ? <img src={item.preview} alt="" /> : <FaFile />}
                            </span>
                            <div className={`${BLOCK}__info`}>
                                <div className={`${BLOCK}__row`}>
                                    <span className={`${BLOCK}__name`} title={item.name}>{item.name}</span>
                                    <span className={`${BLOCK}__size`}>{onFormatBytes(item.size)}</span>
                                </div>
                                {item.status === UPLOAD_STATUS.UPLOADING && <Progress value={item.progress} size="sm" tone="accent" showValue={false} />}
                                {item.error && <p className={`${BLOCK}__error`}>{item.error}</p>}
                                {showFileActions && !item.error && (
                                    <div className={`${BLOCK}__file_actions`}>
                                        {onIsImage(item) ? (
                                            <AppButton variant="ghost" tone="neutral" size="sm" icon={<FaEye />} onClick={() => cb__openViewerCallback(item, 'image')}>Ver imagen</AppButton>
                                        ) : (
                                            <AppButton variant="ghost" tone="neutral" size="sm" icon={<FaFileAlt />} onClick={() => cb__openViewerCallback(item, 'file')}>Ver archivo</AppButton>
                                        )}
                                        <AppButton variant="ghost" tone="neutral" size="sm" icon={<FaDownload />} onClick={() => cb__downloadCallback(item)}>Descargar</AppButton>
                                    </div>
                                )}
                            </div>
                            <div className={`${BLOCK}__item_actions`}>
                                {STATUS_ICON[item.status] && <span className={`${BLOCK}__status`} aria-hidden="true">{STATUS_ICON[item.status]}</span>}
                                {item.status === UPLOAD_STATUS.UPLOADING && (
                                    <AppButton variant="ghost" tone="neutral" size="sm" isIconOnly icon={<FaTimes />} aria-label={`Cancelar ${item.name}`} onClick={() => uploader.onCancel(item.id)} />
                                )}
                                {(item.status === UPLOAD_STATUS.ERROR || item.status === UPLOAD_STATUS.CANCELED) && (
                                    <AppButton variant="ghost" tone="accent" size="sm" isIconOnly icon={<FaRedo />} aria-label={`Reintentar ${item.name}`} onClick={() => uploader.onRetry(item.id)} />
                                )}
                                <AppButton variant="ghost" tone="danger" size="sm" isIconOnly icon={<FaTimes />} aria-label={`Quitar ${item.name}`} onClick={() => uploader.onRemove(item.id)} />
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <AppLightbox
                items={VIEWER_ITEM && useNameViewerModeState === 'image' ? [{
                    src: useNameViewerUrlState, title: VIEWER_ITEM.name, alt: VIEWER_ITEM.name,
                    caption: `${VIEWER_ITEM.type || 'Imagen'} · ${onFormatBytes(VIEWER_ITEM.size)}`,
                }] : []}
                index={0}
                isOpen={Boolean(VIEWER_ITEM) && useNameViewerModeState === 'image'}
                onClose={cb__closeViewerCallback}
                onChangeIndex={() => {}}
                hasZoom
                hasCounter={false}
            />

            <AppModal isOpen={Boolean(VIEWER_ITEM) && useNameViewerModeState === 'file'} onClose={cb__closeViewerCallback}
                title={VIEWER_ITEM?.name ?? 'Visor de archivo'}
                description={VIEWER_ITEM ? `${VIEWER_ITEM.type || 'Archivo'} · ${onFormatBytes(VIEWER_ITEM.size)}` : null}
                size="xl" position="top"
                footer={VIEWER_ITEM ? <AppButton icon={<FaDownload />} onClick={() => cb__downloadCallback(VIEWER_ITEM)}>Descargar</AppButton> : null}>
                {VIEWER_ITEM && useNameViewerModeState === 'file' && (
                    <div className={`${BLOCK}__viewer`} data-mode={useNameViewerModeState}>
                        {onIsVideo(VIEWER_ITEM) && <video src={useNameViewerUrlState} controls />}
                        {onIsAudio(VIEWER_ITEM) && <audio src={useNameViewerUrlState} controls />}
                        {!onIsVideo(VIEWER_ITEM) && !onIsAudio(VIEWER_ITEM) && (onIsPdf(VIEWER_ITEM) || onIsText(VIEWER_ITEM)) && (
                            <iframe src={useNameViewerUrlState} title={`Vista previa de ${VIEWER_ITEM.name}`} />
                        )}
                        {!onIsVideo(VIEWER_ITEM) && !onIsAudio(VIEWER_ITEM) && !onIsPdf(VIEWER_ITEM) && !onIsText(VIEWER_ITEM) && (
                            <div className={`${BLOCK}__viewer_empty`}>
                                <FaFileAlt />
                                <strong>Este formato no tiene vista previa nativa.</strong>
                                <span>Podés descargarlo para abrirlo con la aplicación correspondiente.</span>
                            </div>
                        )}
                    </div>
                )}
            </AppModal>
        </div>
    );
};
